// src/hooks/useAetherisStatus.js
import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';

const POLL_INTERVAL_MS = 30000;

// Single source of truth for live Aetheris system status (including the real
// online core count from the backend's this.cores.size - see aetheris.js's
// enhancedCoreDefinitions and getEnhancedSystemStatus) - shared by every
// component that needs it (the AI assistant panel, the dashboard header badge)
// so there's one poll instead of duplicating this fetch per consumer.
export const useAetherisStatus = () => {
  const [aetherisStatus, setAetherisStatus] = useState(null);
  const [error, setError] = useState(null);
  // Remembers the last count actually seen from a successful check, so a
  // failed poll can say "12 cores unreachable" instead of the misleading
  // "0 Cores Offline" - which reads like zero cores exist, not that all of
  // them are just temporarily unreachable. null until the first success.
  const lastKnownTotalRef = useRef(null);

  const checkAetherisStatus = useCallback(async () => {
    try {
      // Goes through apiService.request() - not a raw fetch() - so an expired
      // access token gets silently refreshed and retried here too.
      const data = await apiService.request('/api/aetheris/status', {
        signal: AbortSignal.timeout(10000)
      });

      const cores = data.coresOnline || data.cores || 0;
      if (cores > 0) lastKnownTotalRef.current = cores;

      setAetherisStatus({
        status: data.aetherisStatus || data.status || 'unknown',
        cores,
        totalCores: lastKnownTotalRef.current,
        connection: data.connection || 'unknown',
        coreHealth: data.platformHealth?.coreHealth || {},
        staffingCoverage: data.staffingCoverage || null
      });
      setError(null);
    } catch (err) {
      console.error('Failed to check Aetheris status:', err.message);
      setError(err.message);
      setAetherisStatus({
        status: 'offline',
        cores: 0,
        totalCores: lastKnownTotalRef.current,
        connection: 'failed'
      });
    }
  }, []);

  useEffect(() => {
    checkAetherisStatus();
    const interval = setInterval(checkAetherisStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkAetherisStatus]);

  return { aetherisStatus, error, checkAetherisStatus };
};
